import type { DataTableColumn, DataTableSortOrder } from './data-table.types';

export type DataTableVariant = 'surface' | 'bordered' | 'plain';

/** Column distribution mode for `app-data-table` (`layout` input). */
export type DataTableLayout = 'default' | 'fluid';

export const DATA_TABLE_DEFAULT_TABLE_CLASS = 'tt-table w-full text-sm';

export function buildDataTableRowClasses(options: {
  rowBaseClass?: string;
  clickableRows: boolean;
  extra?: string;
}): string {
  const parts: string[] = [];
  if (options.rowBaseClass) parts.push(options.rowBaseClass);
  if (options.clickableRows) parts.push('tt-table-row--clickable');
  if (options.extra) parts.push(options.extra);
  return parts.join(' ').trim();
}

const FIXED_WIDTH_COLUMN = /(?:^|\s)(?:w-|max-w-|min-w-\[)/;
const NON_FLEX_COLUMN_IDS = new Set(['actions', 'select', 'expand']);

/**
 * Share of free width after the row-action track is reserved.
 * Flexible text (Název, `nowrap: false`) is 1.5× a single-line column (3:2).
 */
export const DATA_TABLE_FLEX_TRACK_WEIGHT = 3;
export const DATA_TABLE_CLIP_TRACK_WEIGHT = 2;
/**
 * Two icon buttons (2×2rem) + gap + compact cell padding, until the real
 * controls are measured. One-button columns shrink after the first measure.
 */
export const DATA_TABLE_END_TRACK_FALLBACK_PX = 88;
/** Cap so a bad measurement cannot swallow the row. */
export const DATA_TABLE_END_TRACK_MAX_PX = 480;
/** Minimum width (px) when a column is manually resized by dragging its header border. */
export const DATA_TABLE_MIN_COL_WIDTH_PX = 56;
/** Maximum width (px) when a column is manually resized. */
export const DATA_TABLE_MAX_COL_WIDTH_PX = 2400;
/**
 * Readable floor widths for default (non-fluid) lists. Without these, `table-layout:fixed`
 * + `width:100%` crushes many columns to ellipsis and `scrollWidth` never exceeds the card,
 * so horizontal scroll never engages (or oscillates at the boundary).
 */
export const DATA_TABLE_FLEX_FLOOR_PX = 128; // 8rem — matches `.tt-col--flex` min-width
export const DATA_TABLE_CLIP_FLOOR_PX = 88;
export const DATA_TABLE_COMPACT_FLOOR_PX = 68; // 4.25rem — matches `.tt-col--compact`
export const DATA_TABLE_METRIC_FLOOR_PX = 72;

export type DataTableTrackKind = 'end' | 'flex' | 'clip';

export function clampDataTableColumnWidthPx(px: number): number {
  if (!Number.isFinite(px)) return DATA_TABLE_MIN_COL_WIDTH_PX;
  return Math.min(Math.max(Math.round(px), DATA_TABLE_MIN_COL_WIDTH_PX), DATA_TABLE_MAX_COL_WIDTH_PX);
}

/**
 * Structural column equality for change detection.
 * Parents often bind `[columns]="buildColumns()"` getters that allocate a new array every CD;
 * comparing ids avoids tearing down every row via `rowRenderEpoch`.
 */
export function dataTableColumnStructureEquals<T>(
  a: readonly DataTableColumn<T>[] | null | undefined,
  b: readonly DataTableColumn<T>[] | null | undefined,
): boolean {
  const left = a ?? [];
  const right = b ?? [];
  if (left === right) return true;
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i++) {
    if ((left[i]?.id ?? '') !== (right[i]?.id ?? '')) return false;
  }
  return true;
}

/**
 * Whether a column header shows a drag handle for manual resizing.
 * Narrow action / selection / expand columns stay content-sized.
 */
export function isDataTableColumnResizable(column: {
  id: string;
  label?: string;
  resizable?: boolean;
  headerClass?: string;
  cellClass?: string;
}): boolean {
  if (column.resizable !== undefined) return column.resizable;
  if (NON_FLEX_COLUMN_IDS.has(column.id) || column.id === 'checkbox') return false;
  const classes = `${column.headerClass ?? ''} ${column.cellClass ?? ''}`;
  if (!column.label?.trim() && /\bw-(?:8|10|12)\b/.test(classes)) return false;
  return true;
}

/**
 * Text column that may shrink and wrap (title + second line).
 * Columns with an explicit width, and action/selection columns, stay fixed.
 */
export function dataTableColumnIsFlexible(column: {
  id: string;
  nowrap?: boolean;
  headerClass?: string;
  cellClass?: string;
}): boolean {
  if (column.nowrap !== false) return false;
  if (NON_FLEX_COLUMN_IDS.has(column.id)) return false;
  const classes = `${column.headerClass ?? ''} ${column.cellClass ?? ''}`;
  return !FIXED_WIDTH_COLUMN.test(classes);
}

export type DataTableTrackColumn = {
  id: string;
  label?: string;
  resizable?: boolean;
  nowrap?: boolean;
  headerClass?: string;
  cellClass?: string;
};

/** Row-action / selection stays content-sized. Text that may wrap is the wider track. */
export function dataTableColumnTrackKind(column: DataTableTrackColumn): DataTableTrackKind {
  if (NON_FLEX_COLUMN_IDS.has(column.id)) return 'end';
  return dataTableColumnIsFlexible(column) ? 'flex' : 'clip';
}

/**
 * Fixed tracks that sum to the table width.
 * End columns are a px length (buttons). Manually resized columns stay locked
 * at their chosen pixel width. Every remaining column shares `100% - fixedPx`
 * in proportion to its weight.
 */
export function dataTableColumnTrackWidths(
  columns: readonly DataTableTrackColumn[],
  endPxById: Readonly<Record<string, number>> = {},
  manualPxById: Readonly<Record<string, number>> = {},
): string[] {
  if (columns.length === 0) return [];
  const kinds = columns.map((column) => dataTableColumnTrackKind(column));
  const endPx = kinds.map((kind, index) => {
    if (kind !== 'end') return 0;
    const measured = endPxById[columns[index].id];
    const px = Math.round(measured && measured > 0 ? measured : DATA_TABLE_END_TRACK_FALLBACK_PX);
    return Math.min(Math.max(px, 1), DATA_TABLE_END_TRACK_MAX_PX);
  });
  const manualPx = kinds.map((kind, index) => {
    if (kind === 'end') return 0;
    const raw = manualPxById[columns[index].id];
    if (raw == null || !(raw > 0)) return 0;
    return clampDataTableColumnWidthPx(raw);
  });
  const totalEnd = endPx.reduce((sum, px) => sum + px, 0);
  const totalManual = manualPx.reduce((sum, px) => sum + px, 0);
  const totalFixed = totalEnd + totalManual;

  let totalWeight = 0;
  for (let i = 0; i < kinds.length; i++) {
    if (kinds[i] === 'end' || manualPx[i] > 0) continue;
    if (kinds[i] === 'flex') totalWeight += DATA_TABLE_FLEX_TRACK_WEIGHT;
    else if (kinds[i] === 'clip') totalWeight += DATA_TABLE_CLIP_TRACK_WEIGHT;
  }
  if (totalWeight <= 0) {
    if (totalManual > 0) {
      return kinds.map((kind, index) => (kind === 'end' ? `${endPx[index]}px` : `${manualPx[index]}px`));
    }
    return columns.map(() => `calc(100% / ${columns.length})`);
  }
  const basis = totalFixed > 0 ? `(100% - ${totalFixed}px)` : '100%';
  return kinds.map((kind, index) => {
    if (kind === 'end') return `${endPx[index]}px`;
    if (manualPx[index] > 0) return `${manualPx[index]}px`;
    const weight = kind === 'flex' ? DATA_TABLE_FLEX_TRACK_WEIGHT : DATA_TABLE_CLIP_TRACK_WEIGHT;
    return `calc(${basis} * ${weight} / ${totalWeight})`;
  });
}

/** True when the column uses dense checkbox / metric chrome (not a full text track). */
export function dataTableColumnHasDenseChrome(column: DataTableTrackColumn): 'compact' | 'metric' | null {
  const classes = `${column.headerClass ?? ''} ${column.cellClass ?? ''}`;
  if (/\btt-col--compact\b/.test(classes)) return 'compact';
  if (/\btt-col--metric\b/.test(classes)) return 'metric';
  return null;
}

/**
 * Readable minimum width for one column (manual resize wins; end columns use measured px).
 * Sum of floors is the table `min-width` so wide lists scroll instead of crushing.
 */
export function dataTableColumnFloorPx(
  column: DataTableTrackColumn,
  endPxById: Readonly<Record<string, number>> = {},
  manualPxById: Readonly<Record<string, number>> = {},
): number {
  const kind = dataTableColumnTrackKind(column);
  if (kind === 'end') {
    const measured = endPxById[column.id];
    const px = Math.round(measured && measured > 0 ? measured : DATA_TABLE_END_TRACK_FALLBACK_PX);
    return Math.min(Math.max(px, 1), DATA_TABLE_END_TRACK_MAX_PX);
  }
  const rawManual = manualPxById[column.id];
  if (rawManual != null && rawManual > 0) {
    return clampDataTableColumnWidthPx(rawManual);
  }
  const dense = dataTableColumnHasDenseChrome(column);
  if (dense === 'compact') return DATA_TABLE_COMPACT_FLOOR_PX;
  if (dense === 'metric') return DATA_TABLE_METRIC_FLOOR_PX;
  if (kind === 'flex') return DATA_TABLE_FLEX_FLOOR_PX;
  return DATA_TABLE_CLIP_FLOOR_PX;
}

/** Sum of per-column readable floors (always ≥ 0). */
export function dataTableContentMinWidthPx(
  columns: readonly DataTableTrackColumn[],
  endPxById: Readonly<Record<string, number>> = {},
  manualPxById: Readonly<Record<string, number>> = {},
): number {
  if (columns.length === 0) return 0;
  return columns.reduce(
    (sum, column) => sum + dataTableColumnFloorPx(column, endPxById, manualPxById),
    0,
  );
}

/**
 * Table sizing for default lists:
 * - `minWidthPx` — always a content floor so many columns scroll horizontally instead of
 *   crushing under `width:100%` / `table-layout:fixed`. Short lists stay `width:100%` and
 *   expand above the floor.
 * - `explicitWidthPx` — when every non-end column is manually sized (locked pixel sum).
 */
export function dataTableManualTableSizing(
  columns: readonly DataTableTrackColumn[],
  endPxById: Readonly<Record<string, number>> = {},
  manualPxById: Readonly<Record<string, number>> = {},
): { minWidthPx: number | null; explicitWidthPx: number | null } {
  if (columns.length === 0) return { minWidthPx: null, explicitWidthPx: null };

  let totalEnd = 0;
  let totalManual = 0;
  let manualCount = 0;
  let unresizedNonEnd = 0;

  for (const column of columns) {
    const kind = dataTableColumnTrackKind(column);
    if (kind === 'end') {
      totalEnd += dataTableColumnFloorPx(column, endPxById, manualPxById);
      continue;
    }
    const rawManual = manualPxById[column.id];
    if (rawManual != null && rawManual > 0) {
      totalManual += clampDataTableColumnWidthPx(rawManual);
      manualCount++;
      continue;
    }
    unresizedNonEnd++;
  }

  const minWidthPx = dataTableContentMinWidthPx(columns, endPxById, manualPxById);
  const explicitWidthPx =
    manualCount > 0 && unresizedNonEnd === 0 ? totalEnd + totalManual : null;
  return {
    minWidthPx: minWidthPx > 0 ? minWidthPx : null,
    explicitWidthPx,
  };
}
const COLUMN_WIDTHS_STORAGE_PREFIX = 'tt-col-widths:v1:';

export function buildDataTableResizeStorageKey(
  columns: readonly { id: string }[],
  customKey?: string,
  pathname?: string,
): string | null {
  const ids = columns.map((c) => c.id).filter(Boolean);
  if (ids.length === 0) return null;
  const scope = customKey?.trim() || pathname?.trim() || '/';
  return `${COLUMN_WIDTHS_STORAGE_PREFIX}${scope}:${ids.join(',')}`;
}

export function loadStoredDataTableColumnWidths(
  storageKey: string | null,
  columns: readonly DataTableTrackColumn[],
): { manualColumnWidths: Record<string, number>; userResizedColumnIds: Set<string> } {
  const empty = { manualColumnWidths: {}, userResizedColumnIds: new Set<string>() };
  if (!storageKey || typeof window === 'undefined') return empty;
  try {
    const raw = window.localStorage?.getItem(storageKey);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as {
      manual?: Record<string, unknown>;
      user?: unknown[];
    } | null;
    if (!parsed || typeof parsed !== 'object') return empty;
    const allowedIds = new Set(
      columns.filter((c) => isDataTableColumnResizable(c)).map((c) => c.id),
    );
    const manualColumnWidths: Record<string, number> = {};
    for (const [id, val] of Object.entries(parsed.manual ?? {})) {
      if (!allowedIds.has(id) || typeof val !== 'number' || !Number.isFinite(val) || val <= 0) {
        continue;
      }
      manualColumnWidths[id] = clampDataTableColumnWidthPx(val);
    }
    const userResizedColumnIds = new Set<string>();
    if (Array.isArray(parsed.user)) {
      for (const id of parsed.user) {
        if (typeof id === 'string' && id in manualColumnWidths) {
          userResizedColumnIds.add(id);
        }
      }
    }
    if (Object.keys(manualColumnWidths).length > 0 && userResizedColumnIds.size === 0) {
      for (const id of Object.keys(manualColumnWidths)) {
        userResizedColumnIds.add(id);
      }
    }
    return { manualColumnWidths, userResizedColumnIds };
  } catch {
    return empty;
  }
}

export function saveStoredDataTableColumnWidths(
  storageKey: string | null,
  manualColumnWidths: Readonly<Record<string, number>>,
  userResizedColumnIds: ReadonlySet<string>,
): void {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    const entries = Object.entries(manualColumnWidths).filter(
      ([, px]) => typeof px === 'number' && Number.isFinite(px) && px > 0,
    );
    if (entries.length === 0 || userResizedColumnIds.size === 0) {
      window.localStorage?.removeItem(storageKey);
      return;
    }
    const payload = {
      manual: Object.fromEntries(entries),
      user: Array.from(userResizedColumnIds),
    };
    window.localStorage?.setItem(storageKey, JSON.stringify(payload));
  } catch {
    // Ignore storage quota / security errors.
  }
}

/** Clip overflow into the next cell. Fluid matrices keep their own column tracks. */
export function dataTableColumnFitClass(
  column: DataTableTrackColumn,
  layout: DataTableLayout = 'default',
): string {
  const box = 'min-w-0 overflow-hidden';
  if (layout === 'fluid') return box;
  if (dataTableColumnTrackKind(column) === 'end') return 'tt-col--end';
  const track = dataTableColumnTrackKind(column) === 'flex' ? 'tt-col--flex' : 'tt-col--clip';
  return `${track} ${box}`;
}

/**
 * Border-box width of one row-action cell, from its buttons rather than the
 * stretched column. Returns 0 when the cell has no content yet.
 *
 * Multiple direct children (e.g. map `app-button` + delete without a flex
 * wrapper) are summed like an inline row — measuring only the first child
 * left the Akce track at one-button width and clipped the rest.
 */
export function measureDataTableEndCellPx(cell: HTMLTableCellElement): number {
  const chrome = horizontalChrome(cell);
  const kids = Array.from(cell.children) as HTMLElement[];
  let inner = 0;
  if (kids.length === 0) {
    inner = Math.max(0, cell.scrollWidth - chrome);
  } else if (kids.length === 1) {
    inner = measureControlPx(kids[0]);
  } else {
    inner = kids.reduce((sum, kid) => sum + measureControlPx(kid), 0);
  }
  const fromScroll = cell.scrollWidth > cell.clientWidth + 1 ? cell.scrollWidth : 0;
  return Math.ceil(Math.max(chrome + inner, fromScroll));
}

/** Widest content in each end column, keyed by column id. */
export function measureDataTableEndTracks(
  table: HTMLTableElement,
  columns: readonly DataTableTrackColumn[],
): Record<string, number> {
  const result: Record<string, number> = {};
  columns.forEach((column, index) => {
    if (dataTableColumnTrackKind(column) !== 'end') return;
    let max = 0;
    for (const row of Array.from(table.rows)) {
      const cell = row.cells[index];
      if (!cell || cell.colSpan !== 1) continue;
      max = Math.max(max, measureDataTableEndCellPx(cell));
    }
    if (max > 0) {
      result[column.id] = Math.min(Math.ceil(max), DATA_TABLE_END_TRACK_MAX_PX);
    }
  });
  return result;
}

function horizontalChrome(el: HTMLElement): number {
  const style = getComputedStyle(el);
  return (
    (Number.parseFloat(style.paddingLeft) || 0) +
    (Number.parseFloat(style.paddingRight) || 0) +
    (Number.parseFloat(style.borderLeftWidth) || 0) +
    (Number.parseFloat(style.borderRightWidth) || 0)
  );
}

function horizontalPadding(el: HTMLElement): number {
  const style = getComputedStyle(el);
  return (Number.parseFloat(style.paddingLeft) || 0) + (Number.parseFloat(style.paddingRight) || 0);
}

function horizontalMargin(el: HTMLElement): number {
  const style = getComputedStyle(el);
  return (Number.parseFloat(style.marginLeft) || 0) + (Number.parseFloat(style.marginRight) || 0);
}

/** Button / icon width, walking flex rows so a stretched wrapper is not the column. */
function measureControlPx(el: HTMLElement): number {
  if (
    el.tagName === 'BUTTON' ||
    el.tagName === 'INPUT' ||
    el.tagName === 'SELECT' ||
    el.tagName === 'IMG' ||
    el.tagName === 'SVG'
  ) {
    return Math.max(el.scrollWidth, el.getBoundingClientRect().width) + horizontalMargin(el);
  }
  const style = getComputedStyle(el);
  const kids = Array.from(el.children) as HTMLElement[];
  const display = style.display;
  const isRow = display === 'flex' || display === 'inline-flex' || display === 'grid';
  if (isRow && kids.length > 0) {
    const gap = Number.parseFloat(style.columnGap) || 0;
    const inner = kids.reduce((sum, kid) => sum + measureControlPx(kid), 0);
    return inner + gap * Math.max(0, kids.length - 1) + horizontalPadding(el) + horizontalMargin(el);
  }
  if (kids.length === 1) return measureControlPx(kids[0]) + horizontalMargin(el);
  return Math.max(el.scrollWidth, el.getBoundingClientRect().width) + horizontalMargin(el);
}

export function dataTableWrapperClass(variant: DataTableVariant): string {
  switch (variant) {
    case 'bordered':
    case 'surface':
      // No tt-surface--overflow: overflow:hidden is a sticky containing block.
      return 'tt-surface min-w-0';
    case 'plain':
    default:
      // Page <main> is the only scrollport; keep this wrapper overflow-visible.
      return 'min-w-0';
  }
}

export function resolveDataTableClass(tableClass: string, layout: DataTableLayout = 'default'): string {
  const base = (tableClass || DATA_TABLE_DEFAULT_TABLE_CLASS).trim();
  if (layout !== 'fluid') return base;
  if (/\btt-table--fluid\b/.test(base)) return base;
  return `${base} tt-table--fluid`.trim();
}

/**
 * Native tooltip for the clipped header label.
 * `headerTitle` wins when the visible label is an abbreviation; otherwise the full label.
 * Empty when there is nothing to read. Sort and filter controls keep their own titles.
 */
export function dataTableHeaderLabelTitle(column: {
  label?: string | null;
  headerTitle?: string | null;
}): string | null {
  const text = (column.headerTitle || column.label || '').trim();
  return text || null;
}

export function dataTableSortButtonAriaLabel<T>(
  column: DataTableColumn<T>,
  sortBy: string,
  sortOrder: DataTableSortOrder,
): string {
  if (sortBy !== column.id) {
    return `Seřadit podle: ${column.label}`;
  }
  const current = sortOrder === 'asc' ? 'vzestupně' : 'sestupně';
  const next = sortOrder === 'asc' ? 'sestupně' : 'vzestupně';
  return `${column.label}, seřazeno ${current}. Přepnout na řazení ${next}.`;
}

export function dataTableShowEmptyFiltered(options: {
  loading: boolean;
  errorMessage: string;
  rowCount: number;
  sourceTotal: number;
}): boolean {
  return (
    !options.loading &&
    !options.errorMessage &&
    options.rowCount === 0 &&
    options.sourceTotal > 0
  );
}

export function dataTableShowEmptySource(options: {
  loading: boolean;
  errorMessage: string;
  rowCount: number;
  sourceTotal: number;
}): boolean {
  return (
    !options.loading &&
    !options.errorMessage &&
    options.rowCount === 0 &&
    options.sourceTotal === 0
  );
}
